import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, ArrowRight, Check, RotateCcw, Search, Loader2 } from "lucide-react";
import { BUSINESS_LABEL } from "../data/businesses";
import { toast } from "sonner";
import { trackEvent } from "../lib/analytics";

const API = process.env.REACT_APP_BACKEND_URL;

// ─────────────────────────────────────────────────────────────────────────────
// Independent diagnostic flow — NOT connected to the existing marketing flow.
// 12 multiple-choice questions split in 4 modules → traffic-light results screen.
// Lives entirely in-memory; no localStorage persistence on purpose (one-shot tool).
// ─────────────────────────────────────────────────────────────────────────────

const WHATSAPP_NUMBER = "527442080244";

// Maps the internal traffic-light color into the user-facing label that goes in
// the WhatsApp asesoría message body. Must stay aligned with LIGHT_STYLES below.
const LIGHT_LABEL_FOR_MSG = {
  green: "BIEN",
  yellow: "ATENCIÓN",
  red: "CRÍTICO",
};

// Builds the WhatsApp asesoría message including the user's diagnostic snapshot
// and a hidden consultant-context block (kept verbatim — Lalo will paste this to
// his LLM tool when he reads the message). Tokens are substituted here in JS;
// the consultant never sees the {PLACEHOLDER} form. When the user reached the
// diagnostic without selecting a giro on Screen 1, giro defaults to
// "negocio local mexicano" as per spec.
function buildAsesoriaMessage({ giro, answers, lights }) {
  const a = answers || {};
  // Priority for {GIRO}:
  //   1. The dedicated giro question answered inside the diagnostic itself.
  //      If the user picked "Otro negocio", we surface the free-text giroOther.
  //   2. Whatever giro they selected on Screen 1 of the marketing flow (passed in).
  //   3. The "negocio local mexicano" default — only when neither is available.
  let resolvedGiro = "";
  if (a.giro === "Otro negocio") {
    resolvedGiro = (a.giroOther || "").trim();
  } else if (a.giro) {
    resolvedGiro = a.giro;
  }
  if (!resolvedGiro && giro && giro.trim()) resolvedGiro = giro.trim();
  if (!resolvedGiro) resolvedGiro = "negocio local mexicano";
  const giroLabel = resolvedGiro;
  const pick = (k) => (a[k] && String(a[k]).trim()) || "no especificado";

  return `[META-PROMPT - NO BORRAR]
Eres un consultor senior de negocios con 20 años de experiencia trabajando con microempresas mexicanas. Combinas las metodologías más rigurosas del mundo:
- Jobs To Be Done (Christensen, Harvard): identificas qué trabajo real contrata el cliente cuando compra en este negocio
- Teoría de Restricciones (Goldratt): encuentras el cuello de botella principal antes de cualquier otra recomendación
- StoryBrand (Donald Miller): posicionas al negocio como guía y al cliente como héroe de su historia
- Business Model Canvas (Osterwalder): evalúas si el modelo de negocio tiene fugas de valor
- Principios de persuasión (Cialdini): aplicas reciprocidad, prueba social y escasez en estrategias concretas
- Ingeniería de prompts nivel experto (guías de Anthropic, Google y OpenAI): estructuras cada respuesta con ROL, CONTEXTO, TAREA, FORMATO y FILTRO

Antes de responder:
1. Lee los datos del negocio completos
2. Identifica el cuello de botella principal usando Teoría de Restricciones
3. Aplica Jobs To Be Done para entender qué busca realmente el cliente final
4. Genera SOLO 3 acciones ejecutables esta semana con lo que ya tiene

REGLAS OBLIGATORIAS:
- Sin tecnicismos que el dueño no entienda
- Sin inversiones adicionales
- Tono: socio de confianza que conoce la realidad del negocio local mexicano
- Español mexicano casual
- Máximo 1 página de respuesta
[FIN META-PROMPT]

Hola Lalo 👋 Hice el diagnóstico de NegoIA. Aquí está el contexto completo de mi negocio:

🏪 TIPO DE NEGOCIO: ${giroLabel}

📊 RESULTADOS DEL DIAGNÓSTICO:
Marketing: ${LIGHT_LABEL_FOR_MSG[lights.marketing]}
Clientes: ${LIGHT_LABEL_FOR_MSG[lights.clientes]}
Digital: ${LIGHT_LABEL_FOR_MSG[lights.digital]}

📋 MIS RESPUESTAS COMPLETAS:
1. Tiempo con el negocio: ${pick("p1")}
2. Clientes al día: ${pick("p2")}
3. Ingresos mensuales: ${pick("p3")}
4. Por qué me eligen: ${pick("p4")}
5. Cómo llegan clientes nuevos: ${pick("p5")}
6. Mis clientes regresan: ${pick("p6")}
7. Mi mayor problema ahora: ${pick("p7")}
8. Personal con el que trabajo: ${pick("p8")}
9. Control de mis costos: ${pick("p9")}
10. Uso de WhatsApp: ${pick("p10")}
11. Publicación en redes: ${pick("p11")}
12. Uso de inteligencia artificial: ${pick("p12")}

🎯 LO QUE NECESITO:
Con base en todo lo anterior, dame:
1. Mi cuello de botella principal (la UNA cosa que más me limita)
2. Qué trabajo real busca mi cliente cuando me compra (Jobs To Be Done)
3. Mis 3 acciones concretas para esta semana con lo que ya tengo
4. Una frase de venta que pueda usar en WhatsApp y redes hoy mismo

📱 FORMATO DE RESPUESTA:
Directo y sin rodeos.
En listas cuando aplique.
Listo para ejecutar esta misma semana.
Tono: como si fuera mi socio de negocio que me conoce hace años.
Sin tecnicismos. Sin inversiones grandes.

Me interesa una asesoría personalizada para trabajar esto juntos 🙌`;
}

const MODULES = [
  {
    title: "Tu giro",
    questions: [
      {
        key: "giro",
        label: "¿Cuál es tu tipo de negocio?",
        options: [
          "Taquería",
          "Pollería",
          "Puesto de comida",
          "Abarrotes / Miscelánea",
          "Bar",
          "Estética / Barbería",
          "Gym funcional",
          "Tienda de ropa",
          "Tortillería",
          "Cafetería",
          "Otro negocio",
        ],
        // When the user picks the value below, surface a text input bound to
        // `customKey` so we capture whatever niche giro they actually run.
        withCustomFor: "Otro negocio",
        customKey: "giroOther",
        customPlaceholder: "Ej: veterinaria, ferretería, papelería...",
      },
    ],
  },
  {
    title: "Tu negocio",
    questions: [
      {
        key: "p1",
        label: "¿Cuánto tiempo llevas con tu negocio?",
        options: ["Menos de 1 año", "1 a 3 años", "Más de 3 años"],
      },
      {
        key: "p2",
        label: "¿Cuántos clientes tienes al día aproximadamente?",
        options: ["Menos de 20", "Entre 20 y 50", "Más de 50"],
      },
      {
        key: "p3",
        label: "¿Cuánto ganas al mes aproximadamente?",
        options: [
          "Menos de $10,000",
          "Entre $10,000 y $30,000",
          "Más de $30,000",
          "Prefiero no decir",
        ],
      },
    ],
  },
  {
    title: "Tu cliente",
    questions: [
      {
        key: "p4",
        label: "¿Por qué te eligen a ti y no a otros?",
        options: ["Por el precio", "Por la calidad", "Por la ubicación", "Porque me conocen"],
      },
      {
        key: "p5",
        label: "¿Cómo llegan tus clientes nuevos?",
        options: [
          "Por recomendación de alguien",
          "Por redes sociales",
          "Me ven pasar",
          "Casi no llegan clientes nuevos",
        ],
      },
      {
        key: "p6",
        label: "¿Tus clientes regresan?",
        options: ["Sí, la mayoría regresa", "Algunos regresan", "Casi no regresan"],
      },
    ],
  },
  {
    title: "Tu operación",
    questions: [
      {
        key: "p7",
        label: "¿Cuál es tu mayor problema ahora mismo?",
        options: [
          "Pocos clientes nuevos",
          "No tengo tiempo para todo",
          "Me falta dinero para crecer",
          "La competencia me está ganando",
        ],
      },
      {
        key: "p8",
        label: "¿Trabajas solo o tienes personal?",
        options: ["Solo yo", "Con 1 o 2 personas", "Con 3 o más personas"],
      },
      {
        key: "p9",
        label: "¿Sabes cuánto te cuesta lo que vendes?",
        options: ["Sí, lo tengo calculado", "Más o menos", "No lo tengo calculado"],
      },
    ],
  },
  {
    title: "Tu presencia digital",
    questions: [
      {
        key: "p10",
        label: "¿Usas WhatsApp para comunicarte con clientes?",
        options: ["Sí, tengo lista de clientes", "A veces", "No lo uso para el negocio"],
      },
      {
        key: "p11",
        label: "¿Publicas en Facebook o Instagram?",
        options: ["Sí, seguido", "A veces", "Casi nunca"],
      },
      {
        key: "p12",
        label: "¿Has usado inteligencia artificial para tu negocio?",
        options: ["Sí, la uso", "Lo he intentado", "No, nunca", "No sé qué es eso"],
      },
    ],
  },
];

// Flat list of all 12 questions in order, each augmented with its module index/title
const FLAT_QUESTIONS = MODULES.flatMap((m, mi) =>
  m.questions.map((q) => ({ ...q, moduleIndex: mi, moduleTitle: m.title })),
);

// ─── Diagnostic logic ───────────────────────────────────────────────────────

function computeLights(a) {
  // Marketing — driven by P11 (publicación en redes)
  let marketing;
  if (a.p11 === "Sí, seguido") marketing = "green";
  else if (a.p11 === "A veces") marketing = "yellow";
  else marketing = "red";

  // Clientes — combina P5 (cómo llegan) y P6 (regresan)
  let clientes;
  if (a.p5 === "Casi no llegan clientes nuevos") {
    clientes = "red";
  } else if (a.p6 === "Sí, la mayoría regresa" && a.p5 === "Por redes sociales") {
    clientes = "green";
  } else if (a.p5 === "Por recomendación de alguien") {
    clientes = "yellow";
  } else if (a.p6 === "Casi no regresan") {
    clientes = "red";
  } else {
    clientes = "yellow";
  }

  // Digital — combina P10 (WhatsApp) y P12 (IA)
  const usesWa = a.p10 === "Sí, tengo lista de clientes";
  const usesAi = a.p12 === "Sí, la uso";
  const noWa = a.p10 === "No lo uso para el negocio";
  const noAi = a.p12 === "No, nunca" || a.p12 === "No sé qué es eso";
  let digital;
  if (usesWa && usesAi) digital = "green";
  else if (noWa && noAi) digital = "red";
  else digital = "yellow";

  return { marketing, clientes, digital };
}

function buildDiagnostic(a) {
  const { p4, p5, p7, p8, p9, p11 } = a;

  if (p7 === "Pocos clientes nuevos") {
    if (p5 === "Casi no llegan clientes nuevos" && p11 === "Casi nunca") {
      return "Tu negocio tiene potencial pero tus clientes nuevos no te encuentran. Tu prioridad es aparecer donde ellos buscan — redes y WhatsApp son tu camino más rápido.";
    }
    if (p5 === "Por recomendación de alguien") {
      return "Hoy creces solo por boca en boca. Es buena señal pero lenta. Si ayudas a esa recomendación con presencia digital activa, los clientes nuevos llegan más rápido.";
    }
    return `Tienes clientes llegando pero no los suficientes. Tu palanca es lo que ya te diferencia (${(p4 || "tu propuesta").toLowerCase()}); el problema es que la mayoría de la gente todavía no lo sabe.`;
  }

  if (p7 === "No tengo tiempo para todo") {
    if (p8 === "Solo yo" && p9 === "No lo tengo calculado") {
      return "Estás haciendo todo solo y eso te limita. Antes de crecer necesitas saber qué actividades te generan más dinero y cuáles te quitan tiempo sin retorno.";
    }
    return "Tu cuello de botella no es el mercado, eres tú. Hay que automatizar o delegar lo que no te genera ingreso directo para liberar horas en lo que sí lo hace.";
  }

  if (p7 === "Me falta dinero para crecer") {
    return "El reto no siempre es crecer — es exprimir lo que ya tienes. Tus clientes actuales pueden comprarte más sin que gastes en publicidad nueva si activas WhatsApp y promos de recompra.";
  }

  if (p7 === "La competencia me está ganando") {
    return `Si te eligen por ${(p4 || "alguna razón").toLowerCase()}, esa es tu palanca. La competencia gana cuando dejas de comunicar lo que te hace único — hay que volverlo visible cada semana.`;
  }

  return "Tu negocio está funcionando, pero hay espacio claro de mejora. Las próximas 3 acciones son tu siguiente paso concreto.";
}

function buildActions(lights, a) {
  const out = [];

  if (lights.marketing === "red") {
    out.push("Publica 3 veces esta semana en Facebook o Instagram: una foto de tu producto, una foto o testimonio de un cliente feliz, y una promo concreta del fin de semana.");
  } else if (lights.marketing === "yellow") {
    out.push("Sube tu frecuencia a 3 publicaciones por semana. Marca los días en tu calendario (ej. lunes, miércoles, viernes) para no fallar.");
  }

  if (lights.clientes === "red") {
    out.push("Pide hoy a 5 clientes actuales que te recomienden con alguien y ofréceles un beneficio si te traen a alguien nuevo (descuento, cortesía, lo que aplique a tu negocio).");
  } else if (lights.clientes === "yellow") {
    out.push("Activa un programa simple de referidos: 'tráeme un amigo y ambos reciben X'. La recomendación es tu canal más fuerte, hay que pedirla, no esperarla.");
  }

  if (lights.digital === "red") {
    out.push("Guarda en WhatsApp a tus 20 mejores clientes esta semana y mándales un mensaje con tu oferta del fin. Es el canal con mayor tasa de respuesta que tienes.");
  } else if (lights.digital === "yellow") {
    out.push("Empieza a usar IA (como ChatGPT o NegoIA) para escribir tus posts semanales. Te ahorra horas y te da ideas frescas sin pensarle.");
  }

  // Fillers para llegar a 3 acciones cuando hay menos de 3 semáforos críticos.
  const fillers = [];
  if (a.p9 !== "Sí, lo tengo calculado") {
    fillers.push("Calcula esta semana cuánto te cuesta producir o entregar una unidad de tu producto principal. Sin ese dato no sabes si estás ganando o perdiendo.");
  }
  fillers.push("Pregunta a 3 clientes frecuentes por qué te eligen. Su respuesta literal es tu mejor frase de venta para redes y WhatsApp.");
  fillers.push("Publica tu horario fijo en WhatsApp y redes. La inconsistencia espanta clientes que no se atreven a preguntar.");

  let i = 0;
  while (out.length < 3 && i < fillers.length) {
    if (!out.includes(fillers[i])) out.push(fillers[i]);
    i++;
  }
  return out.slice(0, 3);
}

// ─── UI components ──────────────────────────────────────────────────────────

const LIGHT_STYLES = {
  green: { bg: "#dcfce7", dot: "#16a34a", label: "Bien", text: "#166534" },
  yellow: { bg: "#fef9c3", dot: "#ca8a04", label: "Atención", text: "#854d0e" },
  red: { bg: "#fee2e2", dot: "#dc2626", label: "Crítico", text: "#991b1b" },
};

function TrafficLight({ name, color, idx }) {
  const s = LIGHT_STYLES[color];
  return (
    <div
      className="flex-1 rounded-xl p-4 flex flex-col items-center gap-2 border-2"
      style={{ backgroundColor: s.bg, borderColor: s.dot }}
      data-testid={`light-${name.toLowerCase()}`}
      data-light-color={color}
    >
      <span
        className="w-5 h-5 rounded-full shadow-inner"
        style={{ backgroundColor: s.dot }}
        aria-hidden="true"
      />
      <span className="text-xs font-extrabold uppercase tracking-wider text-slate-900">
        {name}
      </span>
      <span
        className="text-[11px] font-bold uppercase tracking-wider"
        style={{ color: s.text }}
        data-testid={`light-${name.toLowerCase()}-label`}
      >
        {s.label}
      </span>
      {/* idx is used purely so React keys stay stable */}
      <span hidden>{idx}</span>
    </div>
  );
}

function ResultsView({ answers, giro, onRestart, onBack }) {
  const lights = useMemo(() => computeLights(answers), [answers]);
  const diagnostic = useMemo(() => buildDiagnostic(answers), [answers]);
  const actions = useMemo(() => buildActions(lights, answers), [lights, answers]);
  const asesoriaMsg = useMemo(
    () => buildAsesoriaMessage({ giro, answers, lights }),
    [giro, answers, lights],
  );

  return (
    <div
      className="flex flex-col min-h-[100dvh] px-6 pt-6 pb-10 animate-fade-in-up"
      data-testid="diagnostic-results"
    >
      <button
        type="button"
        onClick={onBack}
        data-testid="diagnostic-results-back"
        className="self-start flex items-center gap-1 -ml-2 px-2 py-2 rounded-lg text-slate-600 active:bg-slate-100"
      >
        <ArrowLeft className="w-5 h-5" strokeWidth={2.2} />
        <span className="text-sm font-medium">Volver al inicio</span>
      </button>

      <span className="inline-flex items-center gap-1.5 mt-4 text-xs font-bold uppercase tracking-wider text-teal-700 bg-teal-50 px-3 py-1 rounded-full self-start">
        <Search className="w-3.5 h-3.5" strokeWidth={2.5} />
        Diagnóstico listo
      </span>

      <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 leading-tight mt-3">
        Aquí está la <span className="text-orange-500">foto de tu negocio</span>
      </h1>

      {/* Three traffic lights */}
      <div className="flex gap-3 mt-6" data-testid="lights-row">
        <TrafficLight name="Marketing" color={lights.marketing} idx={0} />
        <TrafficLight name="Clientes" color={lights.clientes} idx={1} />
        <TrafficLight name="Digital" color={lights.digital} idx={2} />
      </div>

      {/* Diagnostic paragraph */}
      <div
        className="mt-6 rounded-2xl p-5 border-2"
        style={{ backgroundColor: "#FFF7ED", borderColor: "#F97316" }}
        data-testid="diagnostic-paragraph-box"
      >
        <p className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: "#9A3412" }}>
          Tu situación
        </p>
        <p
          className="text-sm text-slate-800 leading-relaxed"
          data-testid="diagnostic-paragraph"
        >
          {diagnostic}
        </p>
      </div>

      {/* Three actions for this week */}
      <h2 className="mt-8 text-xl font-extrabold tracking-tight text-slate-900">
        3 acciones para esta semana
      </h2>
      <ol className="mt-3 flex flex-col gap-3" data-testid="diagnostic-actions">
        {actions.map((a, i) => (
          <li
            key={i}
            className="bg-white border-2 border-slate-200 rounded-xl p-4 flex gap-3"
            data-testid={`diagnostic-action-${i}`}
          >
            <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-teal-600 text-white font-bold text-sm shrink-0">
              {i + 1}
            </span>
            <p className="text-sm text-slate-800 leading-relaxed">{a}</p>
          </li>
        ))}
      </ol>

      {/* Black consulting CTA */}
      <div
        className="mt-8 rounded-2xl p-7 sm:p-8 text-center"
        style={{ backgroundColor: "#1a1a1a" }}
        data-testid="diagnostic-consult-block"
      >
        <h3 className="text-2xl sm:text-[1.7rem] font-extrabold text-white leading-tight">
          ¿Quieres trabajar estas acciones juntos con estrategia personalizada?
        </h3>
        <a
          href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(asesoriaMsg)}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackEvent("wa_consult_diagnostic", {
            giro,
            p7: answers.p7 || "",
            marketing: lights.marketing,
            clientes: lights.clientes,
            digital: lights.digital,
          })}
          data-testid="diagnostic-asesoria-button"
          className="mt-6 w-full inline-flex items-center justify-center text-white font-bold rounded-2xl py-4 active:scale-[0.98] transition-all"
          style={{
            backgroundColor: "#F97316",
            fontSize: "15px",
            boxShadow: "0 10px 20px -6px rgba(249, 115, 22, 0.45)",
          }}
        >
          Quiero una asesoría →
        </a>
        <p
          className="mt-4 text-center leading-snug"
          style={{ color: "#9ca3af", fontSize: "11px" }}
        >
          📲 Respondo en menos de 2 horas · Primera consulta sin costo
        </p>
      </div>

      <button
        type="button"
        onClick={onRestart}
        data-testid="diagnostic-restart"
        className="mt-6 inline-flex items-center justify-center gap-2 text-sm font-semibold text-slate-500 active:text-slate-700 py-2"
      >
        <RotateCcw className="w-4 h-4" strokeWidth={2.2} />
        Hacer el diagnóstico de nuevo
      </button>
    </div>
  );
}

function ContactGate({ submitting, error, onSubmit, onBack }) {
  const [whatsapp, setWhatsapp] = useState("");
  const [nombre, setNombre] = useState("");
  const [consent, setConsent] = useState(false);

  // Mexican WhatsApp numbers are 10 digits. We accept whatever the user types
  // (spaces / dashes / +52) and validate the digit count locally so they get
  // immediate feedback before hitting the API.
  const digits = whatsapp.replace(/\D/g, "");
  const canSubmit = consent && digits.length >= 10 && !submitting;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    onSubmit({ whatsapp: digits, nombre: nombre.trim(), consent: true });
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col min-h-[100dvh] px-6 pt-6 pb-6"
      data-testid="diagnostic-contact-gate"
    >
      <div className="flex items-center justify-between mb-4">
        <button
          type="button"
          onClick={onBack}
          data-testid="contact-gate-back"
          className="flex items-center gap-1 -ml-2 px-2 py-2 rounded-lg text-slate-600 active:bg-slate-100"
        >
          <ArrowLeft className="w-5 h-5" strokeWidth={2.2} />
          <span className="text-sm font-medium">Atrás</span>
        </button>
        <span className="text-sm font-bold text-slate-500">Casi listo</span>
      </div>

      <p
        className="text-xs font-bold uppercase tracking-wider mb-3"
        style={{ color: "#F97316" }}
      >
        Último paso
      </p>
      <h2 className="text-2xl sm:text-[1.7rem] font-bold tracking-tight text-slate-900 leading-snug">
        Te enviamos tu reporte personalizado por WhatsApp
      </h2>
      <p className="text-sm text-slate-500 mt-2 leading-relaxed">
        Necesitamos tu WhatsApp para enviarte el reporte completo del diagnóstico.
      </p>

      <div className="mt-6 flex flex-col gap-4">
        <label className="flex flex-col gap-2">
          <span className="text-sm font-semibold text-slate-700">
            WhatsApp <span className="text-orange-500">*</span>
          </span>
          <input
            type="tel"
            inputMode="numeric"
            value={whatsapp}
            onChange={(e) => setWhatsapp(e.target.value)}
            placeholder="10 dígitos, ej. 7442080244"
            maxLength={20}
            data-testid="contact-whatsapp-input"
            className="w-full p-4 rounded-xl border-2 border-slate-200 bg-slate-50 text-slate-900 text-base focus:border-orange-500 focus:bg-white outline-none transition-all placeholder:text-slate-400"
          />
        </label>

        <label className="flex flex-col gap-2">
          <span className="text-sm font-semibold text-slate-700">
            Nombre <span className="text-slate-400 text-xs font-normal">(opcional)</span>
          </span>
          <input
            type="text"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            placeholder="Cómo te llamamos"
            maxLength={80}
            data-testid="contact-name-input"
            className="w-full p-4 rounded-xl border-2 border-slate-200 bg-slate-50 text-slate-900 text-base focus:border-orange-500 focus:bg-white outline-none transition-all placeholder:text-slate-400"
          />
        </label>

        <label
          className="flex items-start gap-3 cursor-pointer select-none"
          data-testid="contact-consent-label"
        >
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            data-testid="contact-consent-checkbox"
            className="mt-1 w-5 h-5 accent-orange-500 shrink-0"
          />
          <span className="text-sm text-slate-700 leading-snug">
            Acepto que me contacten por WhatsApp con mi reporte{" "}
            <span className="text-orange-500">*</span>
          </span>
        </label>

        {error && (
          <p
            className="text-sm font-semibold text-red-600 leading-snug"
            data-testid="contact-gate-error"
          >
            {error}
          </p>
        )}
      </div>

      <div className="flex-1 min-h-[2rem]" />

      <div className="pt-6 pb-safe">
        <button
          type="submit"
          disabled={!canSubmit}
          data-testid="contact-submit-button"
          className={[
            "w-full flex items-center justify-center gap-2 py-4 rounded-2xl text-lg font-bold transition-all duration-200",
            canSubmit
              ? "bg-orange-500 text-white shadow-lg shadow-orange-500/30 active:scale-[0.98] active:bg-orange-600"
              : "bg-slate-100 text-slate-400 cursor-not-allowed",
          ].join(" ")}
        >
          {submitting ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" strokeWidth={2.5} />
              Enviando...
            </>
          ) : (
            <>
              Ver mi diagnóstico
              <ArrowRight className="w-5 h-5" strokeWidth={2.5} />
            </>
          )}
        </button>
        <p className="text-xs text-slate-400 mt-3 text-center leading-snug">
          🔒 Tus datos se usan solo para enviarte tu reporte personalizado.
        </p>
      </div>
    </form>
  );
}

function QuestionView({ step, answer, answers, onAnswer, onAnswerCustom, onPrev, onNext, onCancel }) {
  const q = FLAT_QUESTIONS[step];
  const total = FLAT_QUESTIONS.length;
  const progressPct = ((step + 1) / total) * 100;
  const showCustom = q.withCustomFor && answer === q.withCustomFor;
  const customValue = q.customKey ? (answers[q.customKey] || "") : "";
  const canContinue = showCustom
    ? customValue.trim().length >= 2
    : Boolean(answer);

  return (
    <div
      className="flex flex-col min-h-[100dvh] px-6 pt-6 pb-6"
      data-testid="diagnostic-question"
      data-question-key={q.key}
    >
      <div className="flex items-center justify-between mb-4">
        <button
          type="button"
          onClick={step === 0 ? onCancel : onPrev}
          data-testid="diagnostic-prev-button"
          className="flex items-center gap-1 -ml-2 px-2 py-2 rounded-lg text-slate-600 active:bg-slate-100"
        >
          <ArrowLeft className="w-5 h-5" strokeWidth={2.2} />
          <span className="text-sm font-medium">{step === 0 ? "Salir" : "Atrás"}</span>
        </button>
        <span
          className="text-sm font-bold text-slate-500"
          data-testid="diagnostic-progress-counter"
        >
          {step + 1} / {total}
        </span>
      </div>

      <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden mb-3">
        <div
          className="h-full rounded-full transition-all duration-500 ease-out"
          style={{ width: `${progressPct}%`, backgroundColor: "#F97316" }}
        />
      </div>

      <p
        className="text-xs font-bold uppercase tracking-wider mb-3"
        style={{ color: "#F97316" }}
        data-testid="diagnostic-module-label"
      >
        Módulo {q.moduleIndex + 1} · {q.moduleTitle}
      </p>

      <div key={step} className="animate-fade-in-up">
        <h2 className="text-2xl sm:text-[1.7rem] font-bold tracking-tight text-slate-900 leading-snug">
          {q.label}
        </h2>

        <div className="mt-6 flex flex-col gap-3">
          {q.options.map((opt) => {
            const isSelected = answer === opt;
            return (
              <button
                key={opt}
                type="button"
                onClick={() => onAnswer(opt)}
                data-testid={`diagnostic-option-${opt}`}
                className={[
                  "flex items-center justify-between text-left p-4 rounded-xl border-2 transition-all duration-150 active:scale-[0.99]",
                  isSelected
                    ? "border-orange-500 bg-orange-50"
                    : "border-slate-200 bg-white active:bg-slate-50",
                ].join(" ")}
              >
                <span
                  className={[
                    "text-base font-semibold leading-snug",
                    isSelected ? "text-orange-900" : "text-slate-800",
                  ].join(" ")}
                >
                  {opt}
                </span>
                {isSelected && (
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-orange-500 text-white shrink-0 ml-3">
                    <Check className="w-4 h-4" strokeWidth={3} />
                  </span>
                )}
              </button>
            );
          })}
          {showCustom && (
            <input
              type="text"
              value={customValue}
              onChange={(e) => onAnswerCustom(q.customKey, e.target.value)}
              placeholder={q.customPlaceholder || "Escribe aquí..."}
              maxLength={80}
              autoFocus
              data-testid="diagnostic-option-custom-input"
              className="w-full p-4 rounded-xl border-2 border-orange-500 bg-white text-slate-900 text-base focus:bg-orange-50 outline-none transition-all placeholder:text-slate-400"
            />
          )}
        </div>
      </div>

      <div className="flex-1 min-h-[2rem]" />

      <div className="pt-6 pb-safe">
        <button
          type="button"
          disabled={!canContinue}
          onClick={onNext}
          data-testid="diagnostic-next-button"
          className={[
            "w-full flex items-center justify-center gap-2 py-4 rounded-2xl text-lg font-bold transition-all duration-200",
            canContinue
              ? "bg-orange-500 text-white shadow-lg shadow-orange-500/30 active:scale-[0.98] active:bg-orange-600"
              : "bg-slate-100 text-slate-400 cursor-not-allowed",
          ].join(" ")}
        >
          {step === total - 1 ? "Continuar" : "Siguiente"}
          <ArrowRight className="w-5 h-5" strokeWidth={2.5} />
        </button>
      </div>
    </div>
  );
}

// ─── Main flow ──────────────────────────────────────────────────────────────

export default function DiagnosticFlow({ onExit, businessType, otherText }) {
  // step: 0..11 (questions). After Q12 the user lands on the contact gate; we
  // only render the results view when the API has acknowledged the submission.
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [phase, setPhase] = useState("questions"); // questions | contact | results
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const total = FLAT_QUESTIONS.length;

  const giro =
    businessType === "otro"
      ? (otherText || "").trim()
      : BUSINESS_LABEL[businessType] || "";

  // Computed lights — used both for the results view and for the contact-gate
  // submission so the backend can store the snapshot the user actually saw.
  const lights = useMemo(() => computeLights(answers), [answers]);

  // Funnel event: fire exactly once when the user enters the diagnostic flow.
  useEffect(() => {
    trackEvent("diagnostic_started", {});
  }, []);

  // Always scroll to top when the step / phase changes.
  useEffect(() => {
    try { window.scrollTo(0, 0); } catch { /* ignore */ }
  }, [step, phase]);

  const submitDiagnostic = async ({ whatsapp, nombre }) => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await fetch(`${API}/api/diagnostics`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          whatsapp,
          nombre,
          consentimiento_whatsapp: true,
          ciudad: "",
          user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
          answers,
          semaforo_marketing: lights.marketing,
          semaforo_clientes: lights.clientes,
          semaforo_digital: lights.digital,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "No pudimos guardar tu diagnóstico. Intenta de nuevo.");
      }
      setPhase("results");
      trackEvent("diagnostic_completed", {
        giro: answers.giro || "",
        marketing: lights.marketing,
        clientes: lights.clientes,
        digital: lights.digital,
      });
      toast.success("¡Listo! Aquí está tu diagnóstico.", { duration: 2000 });
    } catch (err) {
      setSubmitError(err.message || "Error de conexión. Revisa tu internet.");
    } finally {
      setSubmitting(false);
    }
  };

  if (phase === "results") {
    return (
      <ResultsView
        answers={answers}
        giro={giro}
        onRestart={() => {
          setAnswers({});
          setStep(0);
          setPhase("questions");
          setSubmitError(null);
        }}
        onBack={onExit}
      />
    );
  }

  if (phase === "contact") {
    return (
      <ContactGate
        submitting={submitting}
        error={submitError}
        onSubmit={submitDiagnostic}
        onBack={() => {
          setPhase("questions");
          setSubmitError(null);
        }}
      />
    );
  }

  const q = FLAT_QUESTIONS[step];
  const current = answers[q.key];

  // For multiple-choice answers we usually just store the picked value. The one
  // exception is the giro question with its custom "Otro negocio" branch: when
  // the user moves AWAY from "Otro negocio" we proactively drop giroOther so
  // stale text never reaches the final WhatsApp message.
  const handleAnswer = (value) => {
    setAnswers((prev) => {
      const next = { ...prev, [q.key]: value };
      if (q.withCustomFor && q.customKey && value !== q.withCustomFor) {
        delete next[q.customKey];
      }
      return next;
    });
  };

  const handleAnswerCustom = (customKey, value) => {
    setAnswers((prev) => ({ ...prev, [customKey]: value }));
  };

  // Forbid "Siguiente" when the giro question needs a non-empty custom field.
  const showCustom = q.withCustomFor && current === q.withCustomFor;
  const customValue = q.customKey ? (answers[q.customKey] || "") : "";
  const isReady = showCustom ? customValue.trim().length >= 2 : Boolean(current);

  return (
    <QuestionView
      step={step}
      answer={current}
      answers={answers}
      onAnswer={handleAnswer}
      onAnswerCustom={handleAnswerCustom}
      onPrev={() => setStep((s) => Math.max(0, s - 1))}
      onNext={() => {
        if (!isReady) return;
        if (step >= total - 1) {
          setPhase("contact");
        } else {
          setStep((s) => s + 1);
        }
      }}
      onCancel={onExit}
    />
  );
}
